/**
 * 138 FR-011/FR-012 — remembers that the customer declined the install offer, so it is not put in
 * front of them again for a month.
 *
 * A timestamp rather than a boolean: the 30-day window is not expressible without one. The record
 * is per device, per browser, never sent anywhere, and cleared only when the customer clears their
 * site data.
 *
 * Every read and write is guarded. Safari's private mode throws on `setItem`, and storage can be
 * disabled outright — failing to remember a dismissal is a far smaller harm than failing to render
 * the app, so a throw is read as "not dismissed" and the app carries on.
 */
export const INSTALL_DISMISSAL_KEY = 'carii:install-dismissed-at';

/** 30 days, stated once. */
export const INSTALL_DISMISSAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function isDismissed(now: number = Date.now()): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(INSTALL_DISMISSAL_KEY);
  } catch {
    return false;
  }

  if (raw === null || raw === '') return false;

  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;

  // A future timestamp means the device clock moved backwards after the dismissal was written.
  // Treating it as valid would suppress the offer until the clock caught up — indefinitely, for a
  // clock that never does.
  if (dismissedAt > now) return false;

  return now - dismissedAt < INSTALL_DISMISSAL_WINDOW_MS;
}

export function recordDismissal(now: number = Date.now()): void {
  try {
    localStorage.setItem(INSTALL_DISMISSAL_KEY, String(now));
  } catch {
    // Nothing to do and nothing to report: the offer simply reappears on the next visit.
  }
}
